import React from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Footer from "../Footer/FooterBottom";
import { getToken } from "../../context/auth";
import {
    initChatbot,
    deleteChatHistory,
    clearActiveChat,
    startNewChat,
    loadChatSession,
    stopChatStream,
    sendChatMessage
} from "./actions/chatbotActions";

// Dedicated PureComponent for the input section to isolate re-render on typing
class ChatInput extends React.PureComponent {
    state = {
        inputText: ""
    };

    hasNonArabicLetters = (text) => {
        const letters = text.match(/\p{L}/gu);
        if (!letters) return false;
        return letters.some(char => {
            const code = char.charCodeAt(0);
            const isArabicRange = (code >= 0x0600 && code <= 0x06FF) ||
                                  (code >= 0x0750 && code <= 0x077F) ||
                                  (code >= 0x08A0 && code <= 0x08FF) ||
                                  (code >= 0xFB50 && code <= 0xFDFF) ||
                                  (code >= 0xFE70 && code <= 0xFEFF);
            return !isArabicRange;
        });
    };

    handleSend = (e) => {
        if (e) e.preventDefault();
        const { inputText } = this.state;
        const { onSend, loading } = this.props;

        if (inputText.trim().length >= 3 && !this.hasNonArabicLetters(inputText) && !loading) {
            onSend(inputText.trim());
            this.setState({ inputText: "" });
        }
    };

    handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            this.handleSend();
        }
    };

    render() {
        const { inputText } = this.state;
        const { loading, t } = this.props;
        const isInputShort = inputText.length > 0 && inputText.length < 3;
        const isNonArabic = this.hasNonArabicLetters(inputText);

        return (
            <form onSubmit={this.handleSend}>
                <div className="input-container">
                    <textarea
                        value={inputText}
                        onChange={(e) => this.setState({ inputText: e.target.value })}
                        onKeyDown={this.handleKeyPress}
                        placeholder={t("Pages.Chatbot.Placeholder")}
                        className="input-textarea"
                        disabled={loading}
                    />
                    <button
                        type="submit"
                        className="send-button"
                        disabled={loading || inputText.trim().length < 3 || isNonArabic}
                        title={t("Pages.Chatbot.Send")}
                    >
                        <i className="fal fa-paper-plane"></i>
                    </button>
                </div>
                {isNonArabic ? (
                    <div className="warning-message mt-2">
                        <i className="fal fa-exclamation-triangle"></i>
                        <span>{t("Pages.Chatbot.ArabicOnlyError")}</span>
                    </div>
                ) : isInputShort ? (
                    <div className="warning-message mt-2">
                        <i className="fal fa-exclamation-triangle"></i>
                        <span>{t("Pages.Chatbot.MinLettersError")}</span>
                    </div>
                ) : null}
            </form>
        );
    }
}

class Chatbot extends React.PureComponent {
    state = {
        searchTerm: "",
        showSidebar: true,
        confirmDelete: false,
        confirmClear: false,
        fullscreenMessageHtml: null
    };

    messagesEndRef = React.createRef();

    handleKeyDown = (e) => {
        if (e.key === "Escape" && this.state.fullscreenMessageHtml) {
            this.setState({ fullscreenMessageHtml: null });
        }
    };

    componentDidMount() {
        // Initialize chatbot history and restore last active session
        this.props.initChatbot();

        setTimeout(() => this.scrollToBottom(), 300);
        window.addEventListener("keydown", this.handleKeyDown);
    }

    componentWillUnmount() {
        window.removeEventListener("keydown", this.handleKeyDown);
    }

    componentDidUpdate(prevProps) {
        // Scroll to bottom when new messages arrive or current message content changes (streaming)
        const messagesChanged = prevProps.messages.length !== this.props.messages.length;
        const currentMsgStreaming =
            this.props.messages.length > 0 &&
            prevProps.messages.length > 0 &&
            prevProps.messages[prevProps.messages.length - 1].text !== this.props.messages[this.props.messages.length - 1].text;

        if (messagesChanged || currentMsgStreaming) {
            this.scrollToBottom();
        }
    }

    scrollToBottom = () => {
        if (this.messagesEndRef.current) {
            this.messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    handleResend = () => {
        const { messages } = this.props;
        const lastUserMsg = messages.slice().reverse().find(m => m.sender === "user");
        if (lastUserMsg) {
            this.props.sendChatMessage(lastUserMsg.text);
        }
    };

    toggleSidebar = () => {
        this.setState(prevState => ({ showSidebar: !prevState.showSidebar }));
    };

    handleSessionClick = (session) => {
        this.props.loadChatSession(session);
        // On mobile, automatically close sidebar after choosing a session
        if (window.innerWidth < 768) {
            this.setState({ showSidebar: false });
        }
    };

    handleNewChat = () => {
        this.props.startNewChat();
    };

    handleDeleteHistory = () => {
        if (window.confirm(this.props.t("Pages.Chatbot.ConfirmDeleteHistory"))) {
            this.props.deleteChatHistory();
        }
    };

    handleClearChat = () => {
        if (window.confirm(this.props.t("Pages.Chatbot.ConfirmClearChat"))) {
            this.props.clearActiveChat();
        }
    };

    renderLoginRequired = () => {
        const { t } = this.props;
        return (
            <>
                <Helmet>
                    <title>{t("Pages.Chatbot.title")} | {t("alarabeyya")}</title>
                </Helmet>

                <style dangerouslySetInnerHTML={{
                    __html: `
                    .chatbot-page-section {
                        position: relative;
                        overflow: hidden;
                        background: linear-gradient(135deg, rgba(255, 248, 235, 0.9) 0%, rgba(253, 242, 226, 0.9) 100%),
                                    url('/assets/images/about/bg.png') center center / cover no-repeat;
                        padding: 80px 15px;
                        min-height: calc(100vh - 120px);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .chatbot-blob {
                        position: absolute;
                        border-radius: 50%;
                        filter: blur(80px);
                        z-index: 1;
                        opacity: 0.6;
                        animation: float 20s infinite alternate ease-in-out;
                    }
                    .blob-1 {
                        width: 400px;
                        height: 400px;
                        background: radial-gradient(circle, rgba(146, 39, 143, 0.25) 0%, rgba(255,255,255,0) 70%);
                        top: -100px;
                        right: -100px;
                    }
                    .blob-2 {
                        width: 500px;
                        height: 500px;
                        background: radial-gradient(circle, rgba(255, 82, 0, 0.2) 0%, rgba(255,255,255,0) 70%);
                        bottom: -150px;
                        left: -150px;
                        animation-delay: -5s;
                    }
                    @keyframes float {
                        0% { transform: translate(0, 0) scale(1); }
                        50% { transform: translate(40px, -60px) scale(1.1); }
                        100% { transform: translate(-20px, 20px) scale(0.95); }
                    }
                    .lock-card {
                        background: rgba(255, 255, 255, 0.85);
                        backdrop-filter: blur(12px);
                        border: 1px solid rgba(235, 217, 197, 0.5);
                        border-radius: 24px;
                        padding: 50px 30px;
                        max-width: 500px;
                        width: 100%;
                        text-align: center;
                        box-shadow: 0 20px 50px rgba(14, 44, 83, 0.12);
                        z-index: 2;
                        animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    }
                    .lock-icon-container {
                        width: 90px;
                        height: 90px;
                        border-radius: 50%;
                        background: rgba(146, 39, 143, 0.1);
                        color: #92278f;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin: 0 auto 24px;
                        font-size: 36px;
                        animation: pulseLock 2s infinite ease-in-out;
                    }
                    @keyframes pulseLock {
                        0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(146, 39, 143, 0.2); }
                        50% { transform: scale(1.05); box-shadow: 0 0 0 15px rgba(146, 39, 143, 0); }
                    }
                    .lock-title {
                        font-size: 22px;
                        font-weight: 700;
                        color: #0e2c53;
                        margin-bottom: 12px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .lock-text {
                        font-size: 15px;
                        color: #8c857b;
                        line-height: 1.6;
                        margin-bottom: 28px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .login-cta-btn {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        background: linear-gradient(135deg, #92278f 0%, #b23cae 100%);
                        color: #ffffff !important;
                        font-weight: 700;
                        padding: 12px 30px;
                        border-radius: 14px;
                        box-shadow: 0 8px 20px rgba(146, 39, 143, 0.3);
                        transition: all 0.3s ease;
                        text-decoration: none;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .login-cta-btn:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 12px 24px rgba(146, 39, 143, 0.4);
                        text-decoration: none;
                    }
                    .login-cta-btn i {
                        margin-left: 6px;
                        margin-right: 0;
                    }
                `}} />

                <div className="chatbot-page-section">
                    <div className="chatbot-blob blob-1"></div>
                    <div className="chatbot-blob blob-2"></div>
                    <div className="lock-card">
                        <div className="lock-icon-container">
                            <i className="fal fa-user-lock"></i>
                        </div>
                        <h3 className="lock-title">{t("Pages.Chatbot.LoginRequired") || "تسجيل الدخول مطلوب"}</h3>
                        <p className="lock-text">
                            {t("Pages.Chatbot.LoginRequiredDesc") || "يرجى تسجيل الدخول للوصول إلى مساعد الذكاء الاصطناعي والبدء في طرح الأسئلة."}
                        </p>
                        <a href="/Login" className="login-cta-btn">
                            <i className="fal fa-sign-in-alt"></i>
                            <span>{t("Login") || "تسجيل الدخول"}</span>
                        </a>
                    </div>
                </div>
                <Footer />
            </>
        );
    };

    render() {
        // if (!getToken()) {
        //     return this.renderLoginRequired();
        // }

        const { t, messages, history, loading, databaseTimeMs, apiTimeMs, error, errorStatus, activeSessionId } = this.props;
        const { searchTerm, showSidebar, fullscreenMessageHtml } = this.state;

        // Filter the chat sessions list based on search term
        const filteredHistory = history.filter(session =>
            session.title.toLowerCase().includes(searchTerm.toLowerCase())
        );

        return (
            <>
                <Helmet>
                    <title>{t("Pages.Chatbot.title")} | {t("alarabeyya")}</title>
                </Helmet>

                <style dangerouslySetInnerHTML={{
                    __html: `
                    .chatbot-page-section {
                        position: relative;
                        overflow: hidden;
                        background: linear-gradient(135deg, rgba(255, 248, 235, 0.9) 0%, rgba(253, 242, 226, 0.9) 100%),
                                    url('/assets/images/about/bg.png') center center / cover no-repeat;
                        padding: 50px 15px;
                        min-height: calc(100vh - 120px);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .chatbot-blob {
                        position: absolute;
                        border-radius: 50%;
                        filter: blur(80px);
                        z-index: 1;
                        opacity: 0.6;
                        animation: float 20s infinite alternate ease-in-out;
                    }
                    .blob-1 {
                        width: 400px;
                        height: 400px;
                        background: radial-gradient(circle, rgba(146, 39, 143, 0.25) 0%, rgba(255,255,255,0) 70%);
                        top: -100px;
                        right: -100px;
                    }
                    .blob-2 {
                        width: 500px;
                        height: 500px;
                        background: radial-gradient(circle, rgba(255, 82, 0, 0.2) 0%, rgba(255,255,255,0) 70%);
                        bottom: -150px;
                        left: -150px;
                        animation-delay: -5s;
                    }
                    @keyframes float {
                        0% { transform: translate(0, 0) scale(1); }
                        50% { transform: translate(40px, -60px) scale(1.1); }
                        100% { transform: translate(-20px, 20px) scale(0.95); }
                    }
                    .chatbot-wrapper {
                        display: flex;
                        max-width: 1200px;
                        width: 100%;
                        background: rgba(255, 255, 255, 0.85);
                        backdrop-filter: blur(12px);
                        -webkit-backdrop-filter: blur(12px);
                        border: 1px solid rgba(235, 217, 197, 0.5);
                        border-radius: 24px;
                        height: 700px;
                        overflow: hidden;
                        box-shadow: 0 20px 50px rgba(14, 44, 83, 0.12);
                        position: relative;
                        z-index: 2;
                        animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    }
                    @keyframes slideUp {
                        0% { opacity: 0; transform: translateY(40px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                    .chatbot-sidebar {
                        width: 310px;
                        background-color: rgba(253, 250, 245, 0.7);
                        border-left: 1px solid rgba(235, 217, 197, 0.5);
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                        transition: all 0.3s ease;
                        z-index: 10;
                    }
                    .sidebar-header {
                        padding: 25px 20px;
                        border-bottom: 1px solid rgba(235, 217, 197, 0.5);
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }
                    .sidebar-title-row {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                    .sidebar-title {
                        font-size: 18px;
                        font-weight: 700;
                        color: #0e2c53;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin: 0;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .sidebar-title i {
                        color: #92278f;
                    }
                    .sidebar-actions {
                        display: flex;
                        gap: 8px;
                    }
                    .sidebar-search-container {
                        position: relative;
                        display: flex;
                        align-items: center;
                        width: 100%;
                    }
                    .sidebar-search-input {
                        width: 100%;
                        padding: 8px 32px 8px 12px;
                        border-radius: 10px;
                        border: 1px solid rgba(235, 217, 197, 0.6);
                        font-size: 13px;
                        outline: none;
                        background-color: #ffffff;
                        color: #0e2c53;
                        text-align: right;
                        transition: all 0.2s ease;
                        font-family: inherit;
                    }
                    .sidebar-search-input:focus {
                        border-color: #92278f;
                        box-shadow: 0 0 0 3px rgba(146, 39, 143, 0.1);
                    }
                    .sidebar-search-icon {
                        position: absolute;
                        right: 10px;
                        color: #8c857b;
                        font-size: 14px;
                        pointer-events: none;
                    }
                    .sidebar-history-list {
                        flex: 1;
                        overflow-y: auto;
                        padding: 15px;
                    }
                    .history-item {
                        padding: 14px 16px;
                        border-radius: 16px;
                        cursor: pointer;
                        margin-bottom: 10px;
                        background-color: transparent;
                        border: 1px solid transparent;
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        display: flex;
                        flex-direction: column;
                        gap: 6px;
                        text-align: right;
                    }
                    .history-item:hover {
                        background-color: rgba(255, 242, 223, 0.8);
                        border-color: rgba(235, 217, 197, 0.8);
                        transform: translateY(-1px);
                    }
                    .history-item.active {
                        background: linear-gradient(135deg, rgba(146, 39, 143, 0.08) 0%, rgba(255, 82, 0, 0.04) 100%);
                        border-color: rgba(146, 39, 143, 0.3);
                        box-shadow: 0 4px 15px rgba(146, 39, 143, 0.05);
                    }
                    .history-item.active .history-item-title {
                        color: #92278f;
                    }
                    .history-item-title {
                        font-size: 14px;
                        color: #0e2c53;
                        font-weight: bold;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .history-item-date {
                        font-size: 11px;
                        color: #8c857b;
                        display: flex;
                        align-items: center;
                        gap: 4px;
                    }
                    .no-history-text {
                        color: #8c857b;
                        font-size: 14px;
                        text-align: center;
                        margin-top: 30px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .chatbot-main {
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                        background-color: rgba(255, 255, 255, 0.9);
                    }
                    .chat-header {
                        padding: 20px 25px;
                        border-bottom: 1px solid rgba(235, 217, 197, 0.5);
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        background: rgba(253, 250, 245, 0.5);
                    }
                    .chat-header-info {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }
                    .chat-header-title {
                        font-size: 19px;
                        font-weight: 700;
                        color: #0e2c53;
                        margin: 0;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .chat-header-subtitle {
                        font-size: 12px;
                        color: #8c857b;
                        margin: 0;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .chat-header-actions {
                        display: flex;
                        gap: 10px;
                        align-items: center;
                    }
                    .chat-messages-area {
                        flex: 1;
                        overflow-y: auto;
                        padding: 30px;
                        background-color: rgba(255, 252, 248, 0.4);
                        display: flex;
                        flex-direction: column;
                        gap: 24px;
                    }
                    .message-row {
                        display: flex;
                        width: 100%;
                    }
                    .message-row.user {
                        justify-content: flex-start;
                    }
                    .message-row.bot {
                        justify-content: flex-end;
                    }
                    .message-bubble-wrapper {
                        max-width: 80%;
                        display: flex;
                        flex-direction: column;
                    }
                    .message-row.user .message-bubble-wrapper {
                        align-items: flex-start;
                    }
                    .message-row.bot .message-bubble-wrapper {
                        max-width: 100%;
                        width: 100%;
                        align-items: flex-start;
                    }
                    .message-row.bot .message-bubble {
                        width: 100%;
                    }
                    .message-bubble {
                        padding: 16px 22px;
                        line-height: 1.7;
                        font-size: 15.5px;
                        box-shadow: 0 8px 20px rgba(14, 44, 83, 0.02);
                        word-break: break-word;
                        transition: all 0.2s ease;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .message-bubble.user {
                        background: linear-gradient(135deg, #92278f 0%, #b23cae 100%);
                        color: #ffffff;
                        border-radius: 20px 20px 0px 20px;
                        text-align: right;
                        box-shadow: 0 8px 20px rgba(146, 39, 143, 0.25);
                    }
                    .message-bubble.bot {
                        background-color: #fdfaf5;
                        color: #0e2c53;
                        border: 1px solid rgba(235, 217, 197, 0.7);
                        border-radius: 20px 20px 20px 0px;
                        text-align: right;
                    }
                    .bot-answer-html p {
                        margin-bottom: 10px;
                        font-size: 15.5px;
                        color: #0e2c53;
                        line-height: 1.8;
                    }
                    .bot-answer-html p:last-child {
                        margin-bottom: 0;
                    }
                    .bot-answer-html table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 15px 0;
                        border-radius: 12px;
                        overflow: hidden;
                        box-shadow: 0 4px 15px rgba(0,0,0,0.03);
                    }
                    .bot-answer-html th, .bot-answer-html td {
                        padding: 12px 15px;
                        text-align: right;
                        border-bottom: 1px solid #ebd9c5;
                    }
                    .bot-answer-html th {
                        background-color: #0e2c53;
                        color: #ffffff;
                    }
                    .bot-answer-html tr:last-child td {
                        border-bottom: none;
                    }
                    .bot-answer-html tr:nth-child(even) {
                        background-color: rgba(235, 217, 197, 0.15);
                    }
                    .message-stats {
                        display: flex;
                        gap: 10px;
                        font-size: 11px;
                        color: #8c857b;
                        margin-top: 6px;
                    }
                    .stats-badge {
                        background: rgba(241, 237, 228, 0.8);
                        padding: 3px 10px;
                        border-radius: 8px;
                        border: 1px solid rgba(235, 217, 197, 0.6);
                        direction: rtl;
                        display: inline-flex;
                        align-items: center;
                        gap: 4px;
                    }
                    .stats-badge i {
                        color: #ff5200;
                    }
                    .fullscreen-modal-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background-color: rgba(14, 44, 83, 0.65);
                        backdrop-filter: blur(8px);
                        -webkit-backdrop-filter: blur(8px);
                        z-index: 10000;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 30px;
                    }
                    .fullscreen-modal-card {
                        background: rgba(255, 255, 255, 0.95);
                        border: 1px solid rgba(235, 217, 197, 0.6);
                        border-radius: 24px;
                        width: 100%;
                        max-width: 1000px;
                        height: 90vh;
                        display: flex;
                        flex-direction: column;
                        box-shadow: 0 30px 60px rgba(14, 44, 83, 0.25);
                        overflow: hidden;
                    }
                    .fullscreen-modal-header {
                        padding: 20px 30px;
                        border-bottom: 1px solid rgba(235, 217, 197, 0.5);
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        background-color: #fdfaf5;
                    }
                    .fullscreen-modal-title {
                        margin: 0;
                        font-size: 20px;
                        font-weight: 700;
                        color: #0e2c53;
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .fullscreen-modal-close-btn {
                        background: none;
                        border: none;
                        color: #ff5200;
                        font-size: 24px;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.2s ease;
                        width: 40px;
                        height: 40px;
                        border-radius: 50%;
                    }
                    .fullscreen-modal-close-btn:hover {
                        background-color: rgba(255, 82, 0, 0.1);
                        transform: scale(1.1);
                    }
                    .fullscreen-modal-body {
                        flex: 1;
                        padding: 40px;
                        overflow-y: auto;
                        text-align: right;
                        direction: rtl;
                    }
                    .fullscreen-expand-btn {
                        position: sticky;
                        top: 15px;
                        float: left;
                        margin-left: -5px;
                        margin-top: -5px;
                        margin-bottom: 5px;
                        margin-right: 15px;
                        background: rgba(255, 255, 255, 0.75);
                        backdrop-filter: blur(4px);
                        -webkit-backdrop-filter: blur(4px);
                        border: 1px solid rgba(146, 39, 143, 0.25);
                        color: #92278f;
                        width: 36px;
                        height: 36px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        opacity: 0.55;
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        z-index: 10;
                        box-shadow: 0 4px 10px rgba(14, 44, 83, 0.05);
                    }
                    .fullscreen-expand-btn:hover {
                        opacity: 1;
                        background: #ffffff;
                        border-color: #92278f;
                        box-shadow: 0 6px 16px rgba(146, 39, 143, 0.25);
                        transform: scale(1.08);
                    }
                    .chat-input-area {
                        padding: 20px 25px 25px;
                        border-top: 1px solid rgba(235, 217, 197, 0.5);
                        background: rgba(253, 250, 245, 0.5);
                    }
                    .input-container {
                        position: relative;
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                    }
                    .input-textarea {
                        width: 100%;
                        border: 1px solid rgba(235, 217, 197, 0.8);
                        border-radius: 18px;
                        padding: 15px 18px;
                        font-size: 15.5px;
                        resize: none;
                        height: 65px;
                        outline: none;
                        font-family: inherit;
                        transition: all 0.25s ease;
                        background-color: #ffffff;
                        color: #0e2c53;
                        padding-left: 65px;
                        text-align: right;
                        box-shadow: 0 4px 12px rgba(14, 44, 83, 0.03);
                    }
                    .input-textarea:focus {
                        border-color: #92278f;
                        box-shadow: 0 0 0 4px rgba(146, 39, 143, 0.15);
                    }
                    .send-button {
                        position: absolute;
                        left: 12px;
                        top: 12px;
                        width: 40px;
                        height: 40px;
                        border-radius: 12px;
                        border: none;
                        background: linear-gradient(135deg, #8dc63f 0%, #7bb32d 100%);
                        color: #ffffff;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.3s ease;
                        font-size: 18px;
                        box-shadow: 0 4px 12px rgba(141, 198, 63, 0.3);
                    }
                    .send-button:hover:not(:disabled) {
                        background: linear-gradient(135deg, #7bb32d 0%, #689823 100%);
                        transform: translateY(-2px) rotate(-10deg);
                        box-shadow: 0 6px 16px rgba(141, 198, 63, 0.4);
                    }
                    .send-button:disabled {
                        background-color: #e2e8f0;
                        background-image: none;
                        color: #94a3b8;
                        cursor: not-allowed;
                        box-shadow: none;
                        transform: none;
                    }
                    .warning-message {
                        font-size: 12px;
                        color: #ff5200;
                        display: flex;
                        align-items: center;
                        gap: 5px;
                        margin-right: 5px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .typing-indicator {
                        display: flex;
                        align-items: center;
                        gap: 5px;
                        padding: 8px 10px;
                    }
                    .typing-indicator span {
                        width: 8px;
                        height: 8px;
                        background: #92278f;
                        border-radius: 50%;
                        display: inline-block;
                        animation: bounce 1.3s infinite ease-in-out;
                    }
                    .typing-indicator span:nth-child(2) {
                        animation-delay: -1.1s;
                        background: #8dc63f;
                    }
                    .typing-indicator span:nth-child(3) {
                        animation-delay: -0.9s;
                        background: #ff5200;
                    }
                    @keyframes bounce {
                        0%, 80%, 100% { transform: scale(0); }
                        40% { transform: scale(1.0); }
                    }
                    .error-panel {
                        padding: 12px 18px;
                        border-radius: 14px;
                        font-size: 13.5px;
                        margin-bottom: 12px;
                        text-align: right;
                        font-family: "Droid Arabic Naskh", sans-serif;
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
                        border: 1px solid transparent;
                        line-height: 1.5;
                    }
                    .error-panel.api_error {
                        background-color: #fffbeb;
                        border-color: #fde68a;
                        color: #b45309;
                    }
                    .error-panel.server_error {
                        background-color: #fef2f2;
                        border-color: #fecaca;
                        color: #dc2626;
                    }
                    .error-panel.network_error {
                        background-color: #f8fafc;
                        border-color: #e2e8f0;
                        color: #475569;
                    }
                    .error-panel i {
                        font-size: 18px;
                    }
                    .chatbot-btn {
                        border-radius: 12px;
                        padding: 8px 18px;
                        font-size: 14px;
                        font-weight: 700;
                        cursor: pointer;
                        border: none;
                        transition: all 0.25s ease;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        line-height: 1;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .chatbot-btn i {
                        margin-left: 4px;
                        margin-right: 0;
                    }
                    .chatbot-btn-primary {
                        background: linear-gradient(135deg, #92278f 0%, #b23cae 100%);
                        color: #ffffff;
                        box-shadow: 0 4px 12px rgba(146, 39, 143, 0.2);
                    }
                    .chatbot-btn-primary:hover {
                        background: linear-gradient(135deg, #791f76 0%, #9c3398 100%);
                        transform: translateY(-1px);
                        box-shadow: 0 6px 16px rgba(146, 39, 143, 0.3);
                    }
                    .chatbot-btn-danger {
                        background: linear-gradient(135deg, #ff5200 0%, #ff732f 100%);
                        color: #ffffff;
                        box-shadow: 0 4px 12px rgba(255, 82, 0, 0.2);
                    }
                    .chatbot-btn-danger:hover {
                        background: linear-gradient(135deg, #e04700 0%, #f75d16 100%);
                        transform: translateY(-1px);
                        box-shadow: 0 6px 16px rgba(255, 82, 0, 0.3);
                    }
                    .chatbot-btn-outline {
                        background-color: transparent;
                        border: 1px solid rgba(235, 217, 197, 0.8);
                        color: #8c857b;
                    }
                    .chatbot-btn-outline:hover {
                        background-color: rgba(255, 242, 223, 0.6);
                        border-color: rgba(235, 217, 197, 1);
                        color: #0e2c53;
                    }
                    .chatbot-btn-sm {
                        padding: 6px 14px;
                        font-size: 12.5px;
                        border-radius: 10px;
                    }
                    .sidebar-toggle-btn {
                        background: none;
                        border: none;
                        color: #0e2c53;
                        font-size: 20px;
                        cursor: pointer;
                        display: none;
                        align-items: center;
                        justify-content: center;
                    }
                    .empty-chat-state {
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        color: #8c857b;
                        text-align: center;
                        padding: 40px;
                        gap: 20px;
                    }
                    .empty-chat-icon-container {
                        width: 100px;
                        height: 100px;
                        border-radius: 50%;
                        background: rgba(146, 39, 143, 0.05);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-bottom: 10px;
                        animation: bounceEmpty 3s infinite ease-in-out;
                    }
                    .empty-chat-icon {
                        font-size: 44px;
                        color: #92278f;
                    }
                    @keyframes bounceEmpty {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-10px); }
                    }
                    .empty-chat-state h4 {
                        font-size: 20px;
                        font-weight: 700;
                        color: #0e2c53;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .empty-chat-state p {
                        font-size: 15px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    @media (max-width: 768px) {
                        .sidebar-toggle-btn {
                            display: flex;
                        }
                        .chatbot-sidebar {
                            position: absolute;
                            right: 0;
                            top: 0;
                            bottom: 0;
                            transform: translateX(${showSidebar ? "0" : "100%"});
                            box-shadow: -10px 0 30px rgba(0,0,0,0.1);
                        }
                    }
                `}} />

                <section className="chatbot-page-section">
                    {/* Background floating blobs */}
                    <div className="chatbot-blob blob-1"></div>
                    <div className="chatbot-blob blob-2"></div>

                    <div className="chatbot-wrapper">
                        {/* Sidebar: Chat History */}
                        <div className="chatbot-sidebar" style={window.innerWidth < 768 && !showSidebar ? { display: "none" } : {}}>
                            <div className="sidebar-header">
                                <div className="sidebar-title-row">
                                    <h3 className="sidebar-title">
                                        <i className="fal fa-comment-alt-lines"></i>
                                        {t("Pages.Chatbot.History")}
                                    </h3>
                                    {window.innerWidth < 768 && (
                                        <button className="sidebar-toggle-btn" onClick={this.toggleSidebar}>
                                            <i className="fal fa-times"></i>
                                        </button>
                                    )}
                                </div>
                                <div className="sidebar-actions">
                                    <button
                                        className="chatbot-btn chatbot-btn-primary chatbot-btn-sm"
                                        onClick={this.handleNewChat}
                                        style={{ flex: 1 }}
                                    >
                                        <i className="fal fa-plus-circle"></i>
                                        {t("Pages.Chatbot.NewChat")}
                                    </button>
                                    {history.length > 0 && (
                                        <button
                                            className="chatbot-btn chatbot-btn-outline chatbot-btn-sm"
                                            onClick={this.handleDeleteHistory}
                                            title={t("Pages.Chatbot.DeleteHistory")}
                                        >
                                            <i className="fal fa-trash-alt text-danger"></i>
                                        </button>
                                    )}
                                </div>
                                {/* History Search bar */}
                                <div className="sidebar-search-container">
                                    <i className="fal fa-search sidebar-search-icon"></i>
                                    <input
                                        type="text"
                                        className="sidebar-search-input"
                                        placeholder={t("Pages.Chatbot.SearchPlaceholder")}
                                        value={searchTerm}
                                        onChange={(e) => this.setState({ searchTerm: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="sidebar-history-list">
                                {filteredHistory.length === 0 ? (
                                    <div className="no-history-text">
                                        <i className="fal fa-box-open d-block mb-2 h3 text-muted"></i>
                                        {t("Pages.Chatbot.NoHistory")}
                                    </div>
                                ) : (
                                    filteredHistory.map(session => {
                                        const date = new Date(session.timestamp).toLocaleDateString("ar-EG", {
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit"
                                        });
                                        const isActive = session.id === activeSessionId;
                                        return (
                                            <div
                                                key={session.id}
                                                className={`history-item ${isActive ? "active" : ""}`}
                                                onClick={() => this.handleSessionClick(session)}
                                            >
                                                <div className="history-item-title">{session.title}</div>
                                                <div className="history-item-date">
                                                    <i className="fal fa-calendar-alt"></i>
                                                    <span>{date}</span>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Main Chat Area */}
                        <div className="chatbot-main">
                            <div className="chat-header">
                                <div className="chat-header-info">
                                    <button className="sidebar-toggle-btn mr-2" onClick={this.toggleSidebar}>
                                        <i className="fal fa-indent"></i>
                                    </button>
                                    <div>
                                        <h2 className="chat-header-title">
                                            <i className="fal fa-robot text-primary mr-2"></i>
                                            {t("Pages.Chatbot.title")}
                                        </h2>
                                        <p className="chat-header-subtitle">{t("Pages.Chatbot.Subtitle")}</p>
                                    </div>
                                </div>
                                <div className="chat-header-actions">
                                    {messages.length > 0 && (
                                        <button
                                            className="chatbot-btn chatbot-btn-outline chatbot-btn-sm"
                                            onClick={this.handleClearChat}
                                            disabled={loading}
                                        >
                                            <i className="fal fa-trash-undo"></i>
                                            {t("Pages.Chatbot.Clear")}
                                        </button>
                                    )}
                                    {loading && (
                                        <button
                                            className="chatbot-btn chatbot-btn-danger chatbot-btn-sm animate__animated animate__pulse animate__infinite"
                                            onClick={this.props.stopChatStream}
                                        >
                                            <i className="fal fa-stop-circle"></i>
                                            {t("Pages.Chatbot.Stop")}
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="chat-messages-area">
                                {messages.length === 0 ? (
                                    <div className="empty-chat-state">
                                        <div className="empty-chat-icon-container">
                                            <i className="fal fa-comments empty-chat-icon"></i>
                                        </div>
                                        <div>
                                            <h4>{t("Pages.Chatbot.title")}</h4>
                                            <p className="mt-2">{t("Pages.Chatbot.Subtitle")}</p>
                                        </div>
                                    </div>
                                ) : (
                                    messages.map((msg, index) => {
                                        const isBot = msg.sender === "bot";
                                        return (
                                            <div
                                                key={index}
                                                className={`message-row ${msg.sender} animate__animated animate__fadeInUp animate__faster`}
                                            >
                                                <div className="message-bubble-wrapper">
                                                    <div className={`message-bubble ${msg.sender}`}>
                                                        {isBot && !msg.isStreaming && msg.text !== "" && (
                                                            <button
                                                                type="button"
                                                                className="fullscreen-expand-btn"
                                                                onClick={() => this.setState({ fullscreenMessageHtml: msg.text })}
                                                                title={t("Pages.Chatbot.FullScreen") || "عرض بملء الشاشة"}
                                                            >
                                                                <i className="fal fa-expand-arrows"></i>
                                                            </button>
                                                        )}
                                                        {isBot ? (
                                                            msg.text === "" && msg.isStreaming ? (
                                                                <div className="typing-indicator">
                                                                    <span></span>
                                                                    <span></span>
                                                                    <span></span>
                                                                </div>
                                                            ) : (
                                                                <div
                                                                    className="bot-answer-html"
                                                                    dangerouslySetInnerHTML={{ __html: msg.text }}
                                                                />
                                                            )
                                                        ) : (
                                                            <div>{msg.text}</div>
                                                        )}
                                                    </div>
                                                    {isBot && !msg.isStreaming && (msg.databaseTimeMs > 0 || msg.apiTimeMs > 0) && (
                                                        <div className="message-stats">
                                                            {msg.databaseTimeMs !== undefined && msg.databaseTimeMs > 0 && (
                                                                <span className="stats-badge">
                                                                    <i className="fal fa-database"></i>
                                                                    <span>
                                                                        {t("Pages.Chatbot.DbTime")} {(msg.databaseTimeMs / 1000).toFixed(2)} {t("Pages.Chatbot.Sec")}
                                                                    </span>
                                                                </span>
                                                            )}
                                                            {msg.apiTimeMs !== undefined && msg.apiTimeMs > 0 && (
                                                                <span className="stats-badge">
                                                                    <i className="fal fa-server"></i>
                                                                    <span>
                                                                        {t("Pages.Chatbot.ApiTime")} {(msg.apiTimeMs / 1000).toFixed(2)} {t("Pages.Chatbot.Sec")}
                                                                    </span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                <div ref={this.messagesEndRef} />
                            </div>

                            <div className="chat-input-area">
                                {error && (
                                    <div className={`error-panel ${errorStatus || ""}`}>
                                        <i className={
                                            errorStatus === "api_error"
                                                ? "fal fa-robot animate__animated animate__shakeX"
                                                : errorStatus === "server_error"
                                                    ? "fal fa-server animate__animated animate__shakeX"
                                                    : "fal fa-wifi-slash animate__animated animate__flash animate__infinite"
                                        }></i>
                                        <div style={{ flex: 1 }}>
                                            <strong>
                                                {errorStatus === "api_error"
                                                    ? t("Pages.Chatbot.AiError")
                                                    : errorStatus === "server_error"
                                                        ? t("Pages.Chatbot.ServerError")
                                                        : t("Pages.Chatbot.ConnectionError")}
                                            </strong>
                                            {error}
                                        </div>
                                        <button
                                            type="button"
                                            className="chatbot-btn chatbot-btn-primary chatbot-btn-sm"
                                            onClick={this.handleResend}
                                            style={{ alignSelf: "center" }}
                                        >
                                            <i className="fal fa-redo"></i>
                                            <span>{t("Pages.Chatbot.Resend")}</span>
                                        </button>
                                    </div>
                                )}
                                <ChatInput onSend={this.props.sendChatMessage} loading={loading} t={t} />
                            </div>
                        </div>
                    </div>
                {fullscreenMessageHtml && (
                    <div className="fullscreen-modal-overlay animate__animated animate__fadeIn animate__faster" onClick={() => this.setState({ fullscreenMessageHtml: null })}>
                        <div className="fullscreen-modal-card animate__animated animate__zoomIn animate__faster" onClick={(e) => e.stopPropagation()}>
                            <div className="fullscreen-modal-header">
                                <h3 className="fullscreen-modal-title">
                                    <i className="fal fa-robot text-primary"></i>
                                    {t("Pages.Chatbot.FullScreenTitle") || "تفاصيل الإجابة بملء الشاشة"}
                                </h3>
                                <button
                                    className="fullscreen-modal-close-btn"
                                    onClick={() => this.setState({ fullscreenMessageHtml: null })}
                                    title={t("cancel") || "إغلاق"}
                                >
                                    <i className="fal fa-times"></i>
                                </button>
                            </div>
                            <div className="fullscreen-modal-body bot-answer-html" dangerouslySetInnerHTML={{ __html: fullscreenMessageHtml }} />
                        </div>
                    </div>
                )}
                </section>
                <Footer />
            </>
        );
    }
}

const mapStateToProps = state => {
    return {
        messages: state.chatbot.messages,
        history: state.chatbot.history,
        loading: state.chatbot.loading,
        databaseTimeMs: state.chatbot.databaseTimeMs,
        apiTimeMs: state.chatbot.apiTimeMs,
        error: state.chatbot.error,
        errorStatus: state.chatbot.errorStatus,
        activeSessionId: state.chatbot.activeSessionId
    };
};

export default connect(mapStateToProps, {
    initChatbot,
    deleteChatHistory,
    clearActiveChat,
    startNewChat,
    loadChatSession,
    stopChatStream,
    sendChatMessage
})(withTranslation("common")(Chatbot));
